<template>
  <form class="auth-step" @submit.prevent="handleSubmit">
    <div class="forgot-back-row">
      <button type="button" class="back-link" @click="$emit('back')">
        <i class="bi bi-arrow-left"></i> Back to Sign In
      </button>
    </div>

    <div class="auth-heading">
      <i class="bi bi-key forgot-icon"></i>
      <h3 class="auth-title">Reset Your Password</h3>
      <p class="auth-subtitle">
        {{ forgotDraft.step === 'mobile'
          ? 'Enter your mobile number and we\'ll send you a reset code'
          : 'Enter the OTP and set a new password'
        }}
      </p>
    </div>

    <div v-if="errorMessage" class="auth-error">
      <i class="bi bi-exclamation-circle"></i>
      {{ errorMessage }}
    </div>

    <!-- ═══ Step 1: Enter mobile number ═══ -->
    <template v-if="forgotDraft.step === 'mobile'">
      <div class="form-inner two mb-20">
        <label><i class="bi bi-phone me-1"></i>Mobile No.</label>
        <div class="mobile-input-row">
          <span class="mobile-prefix">+91</span>
          <input
            :value="mobileDigits"
            type="tel"
            placeholder="Enter mobile number"
            maxlength="10"
            @input="onMobileInput"
          >
        </div>
        <div v-if="fieldErrors.mobile" class="field-error">{{ fieldErrors.mobile }}</div>
      </div>

      <button type="submit" class="primary-btn1 auth-submit-btn" :disabled="sendingOtp">
        <span>{{ sendingOtp ? 'Sending OTP...' : 'Send Reset Code' }}</span>
      </button>
    </template>

    <!-- ═══ Step 2: Enter OTP + new password ═══ -->
    <template v-else-if="forgotDraft.step === 'otp'">
      <div v-if="forgotDraft.mockOtpHint" class="mock-hint mb-20">
        <i class="bi bi-info-circle"></i>
        Test OTP: <strong>{{ forgotDraft.mockOtpHint }}</strong>
      </div>

      <div class="form-inner two mb-20">
        <label>OTP</label>
        <input
          :value="forgotDraft.otp"
          type="text"
          placeholder="Enter 4-digit OTP"
          maxlength="4"
          inputmode="numeric"
          :class="{ 'input-error': !!fieldErrors.otp }"
          @input="updateField('otp', $event.target.value.replace(/\D/g, '').slice(0, 4))"
        >
        <div v-if="fieldErrors.otp" class="field-error">{{ fieldErrors.otp }}</div>
        <div class="otp-meta-row mt-1">
          <span v-if="otpCountdownSeconds > 0" class="otp-timer">
            Expires in {{ otpCountdownSeconds }}s
          </span>
          <span v-else class="otp-expired">OTP expired</span>

          <button
            v-if="canResend"
            type="button"
            class="resend-link"
            @click="$emit('resend-otp')"
          >
            Resend OTP
          </button>
          <span v-else-if="resendCountdownSeconds > 0" class="otp-timer">
            Resend in {{ resendCountdownSeconds }}s
          </span>
        </div>
      </div>

      <div class="form-inner two mb-20">
        <label><i class="bi bi-lock me-1"></i>New Password <span class="req">*</span></label>
        <div class="password-wrapper">
          <input
            :value="forgotDraft.password"
            :type="showPassword ? 'text' : 'password'"
            placeholder="New password"
            autocomplete="new-password"
            :class="{ 'input-error': !!fieldErrors.password }"
            @input="updateField('password', $event.target.value)"
          >
          <button type="button" class="password-toggle" @click="showPassword = !showPassword">
            <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
          </button>
        </div>
        <div v-if="fieldErrors.password" class="field-error">{{ fieldErrors.password }}</div>
        <div v-else class="password-hint">Min 6 characters, one uppercase letter, one special character (e.g. ! @ # $).</div>
      </div>

      <div class="form-inner two mb-20">
        <label><i class="bi bi-lock me-1"></i>Confirm Password <span class="req">*</span></label>
        <div class="password-wrapper">
          <input
            :value="forgotDraft.passwordConfirmation"
            :type="showConfirmPassword ? 'text' : 'password'"
            placeholder="Confirm new password"
            autocomplete="new-password"
            :class="{ 'input-error': !!fieldErrors.passwordConfirmation }"
            @input="updateField('passwordConfirmation', $event.target.value)"
          >
          <button type="button" class="password-toggle" @click="showConfirmPassword = !showConfirmPassword">
            <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
          </button>
        </div>
        <div v-if="fieldErrors.passwordConfirmation" class="field-error">{{ fieldErrors.passwordConfirmation }}</div>
      </div>

      <button type="submit" class="primary-btn1 auth-submit-btn" :disabled="resetting">
        <span>{{ resetting ? 'Resetting...' : 'Reset Password' }}</span>
      </button>
    </template>
  </form>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  forgotDraft: {
    type: Object,
    required: true
  },
  status: {
    type: String,
    default: 'idle'
  },
  errorMessage: {
    type: String,
    default: ''
  },
  fieldErrors: {
    type: Object,
    default: () => ({})
  },
  canResend: {
    type: Boolean,
    default: false
  },
  otpCountdownSeconds: {
    type: Number,
    default: 0
  },
  resendCountdownSeconds: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['send-otp', 'resend-otp', 'reset-password', 'update:forgot-draft', 'back'])

const showPassword = ref(false)
const showConfirmPassword = ref(false)

const sendingOtp = computed(() => props.status === 'sending_otp')
const resetting = computed(() => props.status === 'resetting_password')

const mobileDigits = computed(() => {
  const digits = String(props.forgotDraft.mobile ?? '').replace(/\D/g, '')
  return digits.slice(0, 10)
})

const onMobileInput = (event) => {
  const digits = String(event.target.value ?? '').replace(/\D/g, '').slice(0, 10)
  updateField('mobile', digits)
}

const updateField = (field, value) => {
  emit('update:forgot-draft', {
    ...props.forgotDraft,
    [field]: value
  })
}

const handleSubmit = () => {
  if (props.forgotDraft.step === 'mobile') {
    emit('send-otp')
  } else {
    emit('reset-password')
  }
}
</script>

<style scoped>
.forgot-back-row {
  margin-bottom: 8px;
}

.back-link {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.back-link:hover {
  color: var(--primary-color1, #23b5ee);
}

.forgot-icon {
  font-size: 48px;
  color: var(--primary-color1, #23b5ee);
  display: block;
  text-align: center;
  margin-bottom: 12px;
}

.req {
  color: #dc2626;
}

.otp-meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.otp-timer {
  font-size: 12px;
  color: #6b7280;
}

.otp-expired {
  font-size: 12px;
  color: #ef4444;
  font-weight: 500;
}

.resend-link {
  background: none;
  border: none;
  color: var(--primary-color1, #23b5ee);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
}

.resend-link:hover {
  text-decoration: underline;
}

.mock-hint {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 10px 14px;
  color: #1e40af;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.password-hint {
  color: #9ca3af;
  font-size: 12px;
  margin-top: 4px;
}

.field-error {
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
}

.input-error {
  border-color: #dc2626 !important;
}
.input-error:focus {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1) !important;
}

.password-wrapper {
  position: relative;
}

.password-wrapper input {
  padding-right: 40px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  font-size: 16px;
  line-height: 1;
}

.password-toggle:hover {
  color: #333;
}

.mobile-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-prefix {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
  line-height: 48px;
  padding: 0 10px;
  background: #f9fafb;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
}
</style>
